// components/PerformancesChart.jsx
import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const apiBase = process.env.REACT_APP_API_URL;

function getBadgeClass(rating) {
  if (rating >= 80) return "badge-elite";
  if (rating >= 65) return "badge-high";
  if (rating >= 50) return "badge-mid";
  if (rating >= 40) return "badge-average";
  return "badge-low";
}

function getBarColor(rating) {
  if (rating >= 80) return "bar-elite";
  if (rating >= 65) return "bar-high";
  if (rating >= 50) return "bar-mid";
  if (rating >= 40) return "bar-average";
  return "bar-low";
}

function formatDate(isoString) {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
}

export default function PerformancesChart({ playerId }) {
  const [profileStats, setProfileStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/api/player/${playerId}/profile`);
        if (!cancelled) setProfileStats(res.data);
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const grouped = useMemo(() => {
    const map = profileStats.reduce((acc, s) => {
      const key = s.match_id;
      if (!acc[key]) acc[key] = { date: formatDate(s.start_time), games: [] };
      acc[key].games.push(s);
      return acc;
    }, {});

    Object.values(map).forEach((match) => {
      match.games.sort((a, b) => a.game_number - b.game_number);
    });

    return map;
  }, [profileStats]);

  if (loading) return <p style={{ opacity: 0.5, fontSize: "12px" }}>Loading performances...</p>;
  if (!profileStats.length) return null;

  return (
    <div className="performances-chart-container" style={{ margin: "10px 0", gap: "10px", padding: "10px" }}>
      <span className="avg-label" style={{ fontSize: "11px" }}>
        Performances
      </span>
      <div className="performances-chart" style={{ gap: "6px", height: "110px" }}>
        {Object.entries(grouped).map(([matchId, { date, games }]) => (
          <div className="date-group" key={matchId} style={{ gap: "3px", padding: "0 10px" }}>
            <div className="date-label" style={{ fontSize: "9px" }}>
              {date}
            </div>
            <div className="bars" style={{ gap: "2px", height: "94px" }}>
              {games.map((s, i) => {
                const MIN = 30;
                const heightPct = ((s.rating - MIN) / (100 - MIN)) * 100;
                return (
                  <div className="bar-col" key={i} style={{ width: "20px" }}>
                    <div className="bar-wrap" style={{ height: "50px" }}>
                      <span
                        className={`badge ${getBadgeClass(s.rating)}`}
                        style={{ fontSize: "10px", padding: "1px 2px", width: "20px", boxSizing: "border-box" }}>
                        {s.rating}
                      </span>
                      <div className={`bar ${getBarColor(s.rating)}`} style={{ height: `${heightPct}%`, width: "12px" }} />
                    </div>
                    <div className="bar-meta" style={{ gap: "2px", marginTop: "3px" }}>
                      <span className={`result-dot ${s.result === "W" ? "win" : "loss"}`} style={{ fontSize: "8px" }}>
                        {s.result}
                      </span>
                      <img
                        className="opp-logo"
                        src={`/teams/${s.opponent_team_logo}`}
                        alt={s.opponent_team_name}
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
